import { memo, useMemo } from 'react';

import { BlockEditorGrid } from '@/components/blocks/editor/block-editor-grid';
import { SectionGridRenderer } from '@/components/blocks/renderers/section-grid-renderer';
import { DestructiveText } from '@/components/ui/typography';
import { useIsPageEditor } from '@/hooks/use-is-page-editor';
import { resolveIndexCollectionSectionType } from '@/lib/blocks/collection/index-collection/registry';
import { resolveBlockStackLayout } from '@/lib/blocks/layout';
import { getSectionUiKey } from '@/lib/page-utils';
import { readRecord, sectionPropsForScaledPreview } from '@/lib/prop-utils';
import { getSectionComponent, isBlockBackedSection } from '@/lib/section-registry';
import { getSpecialPageBodyGroup, isSpecialPageLayoutSection } from '@/lib/special-page-layouts';
import type { PageBlock } from '@/types/block';
import type { PageSection } from '@/types/page-section';
import { BlockSectionSurface } from './block-section-surface';
import { PageSectionContext } from './page-section-context';
import { SectionErrorBoundary } from './section-error-boundary';

const EMPTY_BLOCKS: PageBlock[] = [];
const EMPTY_PROPS: Record<string, unknown> = Object.freeze({});

interface PageSectionRendererProps {
  section: PageSection;
  renderMode?: 'default' | 'template_preview' | 'layout_preview';
}

export const PageSectionRenderer = memo(function PageSectionRenderer({
  section,
  renderMode = 'default',
}: PageSectionRendererProps) {
  const isPageEditor = useIsPageEditor();
  const isStaticPreview = renderMode !== 'default';
  const effectiveBlocks = section.blocks ?? EMPTY_BLOCKS;
  const renderComponentWithInternalBlocks = Boolean(resolveIndexCollectionSectionType(section));
  const isBlockBacked =
    !renderComponentWithInternalBlocks &&
    (effectiveBlocks.length > 0 ||
      isBlockBackedSection(section.sectionTemplateCode, section.sectionType));
  const sectionUiKey = getSectionUiKey(section);
  const SectionComponent = useMemo(() => {
    return getSectionComponent(section.sectionTemplateCode, section.sectionType);
  }, [section.sectionTemplateCode, section.sectionType]);

  const baseProps = useMemo(() => readRecord(section.props) ?? EMPTY_PROPS, [section.props]);
  const sectionProps = useMemo(
    () => (isStaticPreview ? sectionPropsForScaledPreview(baseProps) : baseProps),
    [isStaticPreview, baseProps],
  );

  const contextValue = useMemo(
    () => ({
      sectionId: section.id,
      editorSectionUiKey: sectionUiKey,
      renderMode,
      props: sectionProps,
      variant: section.variant ?? undefined,
      blocks: effectiveBlocks,
    }),
    [section.id, sectionUiKey, renderMode, sectionProps, section.variant, effectiveBlocks],
  );
  const structuralRegions = useMemo(() => {
    if (!isSpecialPageLayoutSection(section)) return undefined;
    const bodyGroup = getSpecialPageBodyGroup(section);
    return bodyGroup ? [bodyGroup] : [];
  }, [section]);

  if (!section.visible) {
    return null;
  }

  if (isBlockBacked) {
    const resolvedStackLayout = resolveBlockStackLayout(sectionProps);
    const blockStackLayout =
      sectionProps.backgroundSpacing === 'none'
        ? { ...resolvedStackLayout, verticalPaddingNone: true }
        : resolvedStackLayout;
    const blockContent =
      isPageEditor && !isStaticPreview ? (
        <BlockEditorGrid
          sectionId={section.id}
          editorSectionId={sectionUiKey}
          blocks={effectiveBlocks}
          stackLayout={blockStackLayout}
          isLocked={section.permissions?.locked === true}
          structuralRegions={structuralRegions}
        />
      ) : (
        <SectionGridRenderer blocks={effectiveBlocks} stackLayout={blockStackLayout} />
      );

    const content = (
      <BlockSectionSurface sectionProps={sectionProps}>{blockContent}</BlockSectionSurface>
    );

    return (
      <SectionErrorBoundary
        sectionId={section.id}
        sectionTemplateCode={section.sectionTemplateCode}
      >
        <PageSectionContext.Provider value={contextValue}>{content}</PageSectionContext.Provider>
      </SectionErrorBoundary>
    );
  }

  if (!SectionComponent) {
    console.warn(`Unknown section template code: ${section.sectionTemplateCode}`);
    return (
      <div className="border border-destructive p-4 m-4 rounded-md">
        <DestructiveText className="text-destructive">
          Unknown section type: {section.sectionTemplateCode}
        </DestructiveText>
      </div>
    );
  }

  return (
    <SectionErrorBoundary sectionId={section.id} sectionTemplateCode={section.sectionTemplateCode}>
      <PageSectionContext.Provider value={contextValue}>
        <SectionComponent />
      </PageSectionContext.Provider>
    </SectionErrorBoundary>
  );
});
